import { useMutation, useQueryClient } from "@tanstack/react-query";
import { studyGroupsApi } from "@/lib/humhub-api/studyGroups";
import type {
  LightStudyGroupResponse,
  PaginatedResponse,
  StudyGroupUnreadTotals,
} from "@/lib/humhub-api/types";
import {
  applyOptimisticSetQueriesData,
  restoreQueriesData,
  type QueryDataSnapshot,
} from "@/lib/queryCache/optimisticQueryUtils";
import { studyGroupKeys } from "@/lib/queryKeys/studyGroupKeys";
import {
  EMPTY_STUDY_GROUP_UNREAD_TOTALS,
  applyStudyGroupUnreadTotalsToGroup,
  replaceStudyGroupUnreadTotalsInAggregate,
  toStudyGroupUnreadTotalsFromGroup,
} from "@/lib/studyGroupUnread";
import { promoteStudyGroupInPage } from "@/lib/studyGroupListOrder";

type MyStudyGroupsPage = PaginatedResponse<LightStudyGroupResponse>;

type UpdateStudyGroupLastVisitContext = {
  previousMyStudyGroups: QueryDataSnapshot<MyStudyGroupsPage>[];
  hadUnreadBadge: boolean;
  previousUnreadBadge?: StudyGroupUnreadTotals;
  hadUnreadTotals: boolean;
  previousUnreadTotals?: StudyGroupUnreadTotals;
};

export const useUpdateStudyGroupLastVisit = (studyGroupId: number | null) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["studyGroupLastVisit", studyGroupId],
    mutationFn: async () => {
      if (!studyGroupId) {
        throw new Error("Study group ID is required to update last visit");
      }
      return studyGroupsApi.updateLastVisit(studyGroupId);
    },
    onMutate: async () => {
      if (!studyGroupId) return;
      let previousGroupUnreadTotals: StudyGroupUnreadTotals | undefined;

      const previousMyStudyGroups =
        await applyOptimisticSetQueriesData<MyStudyGroupsPage>(queryClient, {
          cancelFilters: {
            predicate: (query) =>
              studyGroupKeys.isMyStudyGroupsPageKey(query.queryKey),
          },
          snapshotFilters: {
            predicate: (query) =>
              studyGroupKeys.isMyStudyGroupsPageKey(query.queryKey),
          },
          updateFilters: {
            predicate: (query) =>
              studyGroupKeys.isMyStudyGroupsPageKey(query.queryKey),
          },
          updater: (oldData) => {
            if (!oldData) return oldData;

            const groupIndex = oldData.results.findIndex(
              (group) => group.id === studyGroupId,
            );
            if (groupIndex === -1) return oldData;

            const group = oldData.results[groupIndex];
            previousGroupUnreadTotals ??=
              toStudyGroupUnreadTotalsFromGroup(group);
            const updatedGroup = applyStudyGroupUnreadTotalsToGroup(
              group,
              EMPTY_STUDY_GROUP_UNREAD_TOTALS,
            );

            const nextResults = [...oldData.results];
            nextResults[groupIndex] = updatedGroup;
            const pageDataWithUpdatedUnread = {
              ...oldData,
              results: nextResults,
            };

            return promoteStudyGroupInPage(
              pageDataWithUpdatedUnread,
              studyGroupId,
              oldData.page === 1,
              updatedGroup,
            );
          },
        });

      const unreadBadgeKey =
        studyGroupKeys.myStudyGroupsUnreadBadge(studyGroupId);
      const previousUnreadBadge =
        queryClient.getQueryData<StudyGroupUnreadTotals>(unreadBadgeKey);
      previousGroupUnreadTotals ??= previousUnreadBadge;
      const hadUnreadBadge = queryClient.getQueryState(unreadBadgeKey) != null;
      const unreadTotalsKey = studyGroupKeys.myStudyGroupsUnreadTotals();
      const previousUnreadTotals =
        queryClient.getQueryData<StudyGroupUnreadTotals>(unreadTotalsKey);
      const hadUnreadTotals =
        queryClient.getQueryState(unreadTotalsKey) != null;

      queryClient.setQueryData<StudyGroupUnreadTotals>(
        unreadBadgeKey,
        EMPTY_STUDY_GROUP_UNREAD_TOTALS,
      );
      if (previousUnreadTotals && previousGroupUnreadTotals) {
        queryClient.setQueryData<StudyGroupUnreadTotals>(
          unreadTotalsKey,
          replaceStudyGroupUnreadTotalsInAggregate(
            previousUnreadTotals,
            previousGroupUnreadTotals,
            EMPTY_STUDY_GROUP_UNREAD_TOTALS,
          ),
        );
      }

      return {
        previousMyStudyGroups,
        hadUnreadBadge,
        previousUnreadBadge,
        hadUnreadTotals,
        previousUnreadTotals,
      } satisfies UpdateStudyGroupLastVisitContext;
    },
    onError: (_error, _variables, context) => {
      if (context?.previousMyStudyGroups.length) {
        restoreQueriesData(queryClient, context.previousMyStudyGroups);
      }

      if (studyGroupId && context) {
        const unreadBadgeKey =
          studyGroupKeys.myStudyGroupsUnreadBadge(studyGroupId);
        if (context.hadUnreadBadge) {
          queryClient.setQueryData(unreadBadgeKey, context.previousUnreadBadge);
        } else {
          queryClient.removeQueries({ queryKey: unreadBadgeKey, exact: true });
        }
      }

      if (context) {
        const unreadTotalsKey = studyGroupKeys.myStudyGroupsUnreadTotals();
        if (context.hadUnreadTotals) {
          queryClient.setQueryData(
            unreadTotalsKey,
            context.previousUnreadTotals,
          );
        } else {
          queryClient.removeQueries({ queryKey: unreadTotalsKey, exact: true });
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: studyGroupKeys.myStudyGroupsRoot,
        exact: false,
      });

      if (studyGroupId) {
        queryClient.invalidateQueries({
          queryKey: studyGroupKeys.myStudyGroupsUnreadBadge(studyGroupId),
        });
      }
    },
  });

  return mutation;
};
